import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useGetAdminKPIsQuery, useGetAdminRestaurantsQuery } from '../../services/api';
import { selectCurrentUser } from '../../store/slices/authSlice';
import { formatCurrency, formatDate } from '../../utils/helpers';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute';
import Loading from '../../components/Loading/Loading';
import Error from '../../components/Error/Error';
import './Dashboard.css';

const AdminDashboard = () => {
  const user = useSelector(selectCurrentUser);
  const { data: kpisData, isLoading: kpisLoading, error: kpisError } = useGetAdminKPIsQuery();
  const { data: restaurantsData, isLoading: restaurantsLoading } = useGetAdminRestaurantsQuery({
    page: 1,
    limit: 5
  });

  if (kpisLoading) {
    return <Loading text="Loading dashboard..." />;
  }

  if (kpisError) {
    return <Error message="Failed to load dashboard data" />;
  }

  const kpis = kpisData?.data;
  const recentRestaurants = restaurantsData?.data?.restaurants || [];

  return (
    <ProtectedRoute roles={['SUPER_ADMIN']}>
      <div className="container">
        <div className="dashboard-header">
          <h1>Admin Dashboard</h1>
          <p>Welcome back, {user?.name}</p>
        </div>

        {/* KPIs */}
        <div className="kpis-grid">
          <div className="kpi-card">
            <div className="kpi-icon total-restaurants">🏪</div>
            <div className="kpi-content">
              <h3>{kpis?.totalRestaurants || 0}</h3>
              <p>Total Restaurants</p>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon total-orders">📦</div>
            <div className="kpi-content">
              <h3>{kpis?.totalOrders || 0}</h3>
              <p>Total Orders</p>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon total-revenue">💰</div>
            <div className="kpi-content">
              <h3>{formatCurrency(kpis?.totalRevenue || 0)}</h3>
              <p>Total Revenue</p>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon total-customers">👥</div>
            <div className="kpi-content">
              <h3>{kpis?.totalCustomers || 0}</h3>
              <p>Total Customers</p>
            </div>
          </div>
        </div>

        {/* Recent Restaurants */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Recent Restaurants</h2>
            <Link to="/admin/restaurants" className="btn btn-secondary">
              View All
            </Link>
          </div>

          {restaurantsLoading ? (
            <Loading text="Loading restaurants..." />
          ) : (
            <div className="card">
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Shop URL</th>
                      <th>Owner</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRestaurants.map((restaurant) => (
                      <tr key={restaurant._id}>
                        <td>
                          <div className="restaurant-info">
                            {restaurant.logoUrl && (
                              <img 
                                src={`${restaurant.logoUrl}`} 
                                alt={restaurant.name}
                                className="restaurant-logo"
                              />
                            )}
                            <span>{restaurant.name}</span>
                          </div>
                        </td>
                        <td>
                          <code>{restaurant.shopSlug}</code>
                        </td>
                        <td>{restaurant.ownerUserId?.name}</td>
                        <td>
                          <span className={`status-badge status-${restaurant.isActive ? 'active' : 'inactive'}`}>
                            {restaurant.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>{formatDate(restaurant.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            <Link to="/admin/restaurants" className="action-card">
              <div className="action-icon">🏪</div>
              <h3>Manage Restaurants</h3>
              <p>View and manage all restaurants</p>
            </Link>

            <Link to="/admin/customers" className="action-card">
              <div className="action-icon">👥</div>
              <h3>View Customers</h3>
              <p>Browse all customers across restaurants</p>
            </Link>

            <div className="action-card">
              <div className="action-icon">📊</div>
              <h3>Analytics</h3>
              <p>View detailed platform analytics</p>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default AdminDashboard;